/* eslint-disable react/prop-types */
import { useTranslation } from "react-i18next";

import { Check, FileText, Loader2 } from "lucide-react";

/**
 * EPS-4970 revize — girilen ürün fiyatına ve kategorisine uyan ek hizmet paketleri.
 *
 * Kaynak, embed'deki (Bilkom) ek garanti kartlarının aynısı:
 * `GET /additional-service-plans/barem/resolve`. Liste en spesifik eşleşmeden en
 * genele doğru sıralı gelir (ürün → marka → alt kategori → kategori → ana kategori →
 * yalnız fiyat); sıralamayı BE belirlediği için burada yeniden sıralanmıyor.
 *
 * Paket tutarı BE'de hesaplanır (`planPrice`): baremli planda aralığın sabit fiyatı,
 * Standart Planda ürün fiyatı × oran. Panel bu tutarı yalnızca gösterir.
 */
const MATCH_LEVEL_LABELS = {
  MERCHANT_PRODUCT: ["extendedWarranty.add.matchLevel.product", "Bu ürüne özel"],
  SUB_CATEGORY_BRAND: ["extendedWarranty.add.matchLevel.brand", "Markaya özel"],
  SUB_CATEGORY: ["extendedWarranty.add.matchLevel.subCategory", "Alt kategoriye özel"],
  CATEGORY: ["extendedWarranty.add.matchLevel.category", "Kategoriye özel"],
  MAIN_CATEGORY: ["extendedWarranty.add.matchLevel.mainCategory", "Ana kategoriye özel"],
  PRICE_ONLY: ["extendedWarranty.add.matchLevel.priceOnly", "Tüm ürünler için"],
};

function PlanPicker({ plans, loading, error, selectedPlanId, onSelect }) {
  const { t } = useTranslation();

  const formatPrice = (value) =>
    typeof value === "number"
      ? value.toLocaleString("tr-TR", { minimumFractionDigits: 2 })
      : value;

  return (
    <section className="formBox">
      <h2 className="text-lg font-medium text-[#101828]">
        {t("extendedWarranty.add.matchingPlansTitle", "Eşleşen Paketler")}
      </h2>
      <p className="my-2 text-sm text-[#667085]">
        {t(
          "extendedWarranty.add.matchingPlansDescription",
          "Ürün bilgilerine uyan paketler aşağıda listelenir. Satışı yapılacak paketi seçiniz.",
        )}
      </p>

      {loading && (
        <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("extendedWarranty.add.plansLoading", "Paketler getiriliyor...")}
        </div>
      )}

      {/* Bilgi notu kartlarla BİRLİKTE gösterilir: "fiyat aralığı tanımsız paketler
          listelendi" durumunda hem uyarı hem seçilebilir kartlar gerekiyor. */}
      {!loading && error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {error}
        </div>
      )}

      {!loading && plans.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {plans.map((plan) => {
            const selected = String(selectedPlanId) === String(plan.planId);
            const levelLabel = MATCH_LEVEL_LABELS[plan.matchLevel];
            return (
              <button
                type="button"
                key={plan.planId}
                onClick={() => onSelect(plan)}
                className={`flex flex-col gap-3 rounded-lg border p-4 text-left transition ${
                  selected
                    ? "border-[#0040C9] bg-[#F5F8FF] ring-1 ring-[#0040C9]"
                    : "border-[#E3E7EF] hover:border-[#AAC2F7]"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-[#101828]">{plan.name}</span>
                  {selected && (
                    <Check className="h-5 w-5 shrink-0 text-[#0040C9]" />
                  )}
                </div>

                <div className="flex flex-wrap items-baseline gap-x-2">
                  {/* Fiyat aralığı tanımsız eski planlarda tutar plan tanımından
                      çözülemez; satış özetinde alt kategori yüzdesinden hesaplanır. */}
                  {typeof plan.planPrice === "number" ? (
                    <span className="text-2xl font-semibold text-[#0040C9]">
                      {formatPrice(plan.planPrice)} ₺
                    </span>
                  ) : (
                    <span className="text-sm text-[#667085]">
                      {t(
                        "extendedWarranty.add.priceOnSelect",
                        "Tutar paket seçildiğinde hesaplanır",
                      )}
                    </span>
                  )}
                  {plan.durationMonth ? (
                    <span className="text-sm text-[#667085]">
                      {t("extendedWarranty.add.planDuration", "{{month}} ay", {
                        month: plan.durationMonth,
                      })}
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  {levelLabel && (
                    <span className="rounded bg-[#EEF2F8] px-2 py-1 text-[#475467]">
                      {t(levelLabel[0], levelLabel[1])}
                    </span>
                  )}
                  {plan.packageCategory === "OTHER" && (
                    <span className="rounded bg-[#FFF4E5] px-2 py-1 text-[#B54708]">
                      {t("extendedWarranty.add.otherPackage", "Diğer")}
                    </span>
                  )}
                  {plan.packageCode && (
                    <span className="rounded bg-[#EEF2F8] px-2 py-1 text-[#475467]">
                      {plan.packageCode}
                    </span>
                  )}
                  {/* EPS-4971: yalnızca online ödeme linkiyle satılan paket — bayi
                      seçmeden önce görsün, "Satışı Tamamla" bu paketlerde kapalı. */}
                  {Array.isArray(plan.collectionTypes) &&
                    plan.collectionTypes.length > 0 &&
                    !plan.collectionTypes.includes("SELLER") && (
                      <span className="rounded bg-[#EFF4FF] px-2 py-1 text-[#0040C9]">
                        {t(
                          "extendedWarranties.collectionType.online",
                          "Online Ödeme Linki",
                        )}
                      </span>
                    )}
                </div>

                {plan.planDescription && (
                  <p className="text-sm text-[#667085]">{plan.planDescription}</p>
                )}

                {plan.contractPdfUrl && (
                  // Kartın kendisi buton olduğu için tıklama yayılımını kesiyoruz;
                  // sözleşmeye bakmak paket seçmek anlamına gelmemeli.
                  <a
                    href={plan.contractPdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="inline-flex items-center gap-1 text-sm text-[#0049E6] hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    {t("extendedWarranty.add.planContract", "Sözleşme")}
                  </a>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default PlanPicker;
